import { useState } from 'react';
import type { VideoClip, VideoStyle } from './types';
import { useFFmpeg } from './hooks';
import { getFilterForStyle } from './utils/filters';
import { Button } from './components/ui';
import { VideoUploader } from './components/VideoUploader';
import { VideoTrimmer } from './components/VideoTrimmer';
import { StyleSelector } from './components/StyleSelector';
import { MusicUploader } from './components/MusicUploader';
import { PreviewPlayer } from './components/PreviewPlayer';
import { ProgressModal } from './components/ProgressModal';

function App() {
    // State
    const [clips, setClips] = useState<VideoClip[]>([]);
    const [selectedClip, setSelectedClip] = useState<VideoClip | null>(null);
    const [musicFile, setMusicFile] = useState<File | null>(null);
    const [selectedStyle, setSelectedStyle] = useState<VideoStyle>('modern');

    // Trimmer state
    const [isTrimmerOpen, setIsTrimmerOpen] = useState(false);
    const [clipToTrim, setClipToTrim] = useState<VideoClip | null>(null);

    // Generation state
    const [isGenerating, setIsGenerating] = useState(false);
    const [progress, setProgress] = useState(0);
    const [progressMessage, setProgressMessage] = useState('');

    // FFmpeg hook
    const { concatenateVideos, downloadVideo } = useFFmpeg();

    // Handlers
    const handleClipsChange = (newClips: VideoClip[]) => {
        setClips(newClips);
    };

    const handleClipSelect = (clip: VideoClip) => {
        setSelectedClip(clip);
    };

    const handleTrimClick = () => {
        if (selectedClip) {
            setClipToTrim(selectedClip);
            setIsTrimmerOpen(true);
        }
    };

    const handleApplyTrim = (clipId: string, startTime: number, endTime: number) => {
        const updatedClips = clips.map(clip =>
            clip.id === clipId
                ? { ...clip, startTime, endTime }
                : clip
        );
        setClips(updatedClips);

        // Update selected clip if it's the one being trimmed
        if (selectedClip?.id === clipId) {
            setSelectedClip({ ...selectedClip, startTime, endTime });
        }
    };

    const handleMusicUpload = (file: File) => {
        setMusicFile(file);
    };

    const handleGenerateVideo = async () => {
        if (clips.length === 0) {
            alert('Tilføj venligst mindst ét videoklip');
            return;
        }

        setIsGenerating(true);
        setProgress(0);
        setProgressMessage('Starter video generering...');

        try {
            // Get style configuration
            const filter = getFilterForStyle(selectedStyle);

            // Progress callback
            const onProgress = (prog: number, msg: string) => {
                setProgress(prog);
                setProgressMessage(msg);
            };

            // Generate video
            const videoBlob = await concatenateVideos(
                clips,
                filter,
                musicFile,
                onProgress
            );

            // Download the generated video
            downloadVideo(videoBlob, `realestate_video_${Date.now()}.mp4`);

            setProgressMessage('Video færdig!');
            setTimeout(() => {
                setIsGenerating(false);
            }, 1000);
        } catch (error) {
            console.error('Video generation error:', error);
            alert('Fejl ved generering af video');
            setIsGenerating(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-900 flex flex-col">
            {/* Header */}
            <header className="bg-gray-800/50 backdrop-blur-lg border-b border-gray-700 sticky top-0 z-30">
                <div className="container mx-auto px-6 py-4">
                    <div className="flex items-center justify-between">
                        {/* Logo */}
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-accent-500 rounded-lg flex items-center justify-center">
                                <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                            <div>
                                <h1 className="text-xl font-bold gradient-text">RealEstate</h1>
                                <p className="text-xs text-gray-400">Video Pro</p>
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-3">
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={handleTrimClick}
                                disabled={!selectedClip}
                                icon={
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 16 16">
                                        <path d="M2 4H14M2 12H14M6 4V12M10 4V12" stroke="currentColor" strokeWidth="1.5" />
                                    </svg>
                                }
                            >
                                Trim
                            </Button>

                            <Button
                                variant="primary"
                                onClick={handleGenerateVideo}
                                disabled={clips.length === 0 || isGenerating}
                                loading={isGenerating}
                                icon={
                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 20 20">
                                        <path d="M10 4V16M4 10H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                    </svg>
                                }
                            >
                                Generer Video
                            </Button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <div className="flex-1 flex overflow-hidden">
                {/* Left Sidebar - Video Uploader */}
                <VideoUploader
                    onClipsChange={handleClipsChange}
                    onClipSelect={handleClipSelect}
                    maxFiles={10}
                />

                {/* Center - Preview & Music */}
                <main className="flex-1 overflow-y-auto scrollbar-thin p-6 space-y-6">
                    <PreviewPlayer clip={selectedClip} />
                    <MusicUploader onMusicUpload={(file) => handleMusicUpload(file)} />
                </main>

                {/* Right Sidebar - Style Selector */}
                <StyleSelector
                    selectedStyle={selectedStyle}
                    onStyleChange={setSelectedStyle}
                />
            </div>

            {/* Video Trimmer Modal */}
            <VideoTrimmer
                clip={clipToTrim}
                isOpen={isTrimmerOpen}
                onClose={() => setIsTrimmerOpen(false)}
                onApplyTrim={handleApplyTrim}
            />

            {/* Progress Modal */}
            <ProgressModal
                isOpen={isGenerating}
                progress={progress}
                message={progressMessage}
            />
        </div>
    );
}

export default App;
